import React, { useState, useEffect, useCallback } from 'react';
import { Settings, Plus, Edit3, Trash2, Save, X, CheckCircle, AlertCircle, Users, Target, BookOpen } from 'lucide-react';
import api from '../utils/api';

const TaskCategoriesSettings = ({ user }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });

  // Form state for category creation/editing
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#6B46C1',
    // Phase 2: Simplified assignment settings
    default_assignee_id: '',
    domain_id: '',
    assignment_strategy: 'round_robin'
  });

  const [availableUsers, setAvailableUsers] = useState([]);
  const [availableDomains, setAvailableDomains] = useState([]);

  useEffect(() => {
    fetchCategories();
    fetchUsers();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/task-categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch task categories:', error);
      showNotification('error', 'Failed to load task categories');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/team');
      setAvailableUsers(response.data.members || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchDomains = async () => {
    try {
      const response = await api.get('/user-domains');
      setAvailableDomains(response.data);
    } catch (error) {
      console.error('Failed to fetch domains:', error);
    }
  };

  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => setNotification({ show: false, type: '', message: '' }), 5000);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      color: '#6B46C1',
      // Phase 2: Reset assignment settings
      default_assignee_id: '',
      domain_id: '',
      assignment_strategy: 'round_robin'
    });
    setEditingCategory(null);
  };

  const handleAddCategory = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleEditCategory = (category) => {
    setFormData({
      name: category.name || '',
      description: category.description || '',
      color: category.color || '#6B46C1',
      // Phase 2: Load assignment settings
      default_assignee_id: category.default_assignee_id || '',
      domain_id: category.domain_id || '',
      assignment_strategy: category.assignment_strategy || 'round_robin'
    });
    setEditingCategory(category);
    setShowAddModal(true);
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/task-categories/${categoryId}`);
      
      showNotification('success', 'Category deleted successfully');
      fetchCategories();
    } catch (error) {
      console.error('Failed to delete category:', error);
      showNotification('error', 'Failed to delete category');
    }
  };

  const handleSaveCategory = async () => {
    if (!formData.name.trim()) {
      showNotification('error', 'Category name is required');
      return;
    }

    setSaving(true);
    try {
      const categoryData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        color: formData.color
      };

      if (editingCategory) {
        await api.put(`/task-categories/${editingCategory.id}`, categoryData);
        
        // Update assignment settings separately (Phase 2)
        if (formData.assignment_strategy) {
          await api.put(`/task-categories/${editingCategory.id}/assignment`, {
            default_assignee_id: formData.default_assignee_id || null,
            domain_id: formData.domain_id || null,
            assignment_strategy: formData.assignment_strategy
          });
        }
        
        showNotification('success', 'Category updated successfully');
      } else {
        const response = await api.post('/task-categories', categoryData);
        
        // Set assignment settings for new category (Phase 2)
        if (response.data.id && formData.assignment_strategy) {
          await api.put(`/task-categories/${response.data.id}/assignment`, {
            default_assignee_id: formData.default_assignee_id || null,
            domain_id: formData.domain_id || null,
            assignment_strategy: formData.assignment_strategy
          });
        }
        
        showNotification('success', 'Category created successfully');
      }

      setShowAddModal(false);
      resetForm();
      fetchCategories();
    } catch (error) {
      console.error('Failed to save category:', error);
      showNotification('error', 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const addSkill = (skill) => {
    if (skill.trim() && !formData.metadata.required_skills.includes(skill.trim())) {
      setFormData(prev => ({
        ...prev,
        metadata: {
          ...prev.metadata,
          required_skills: [...prev.metadata.required_skills, skill.trim()]
        }
      }));
    }
  };

  const removeSkill = (skillToRemove) => {
    setFormData(prev => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        required_skills: prev.metadata.required_skills.filter(skill => skill !== skillToRemove)
      }
    }));
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-6 bg-gray-200 rounded w-1/4"></div>
        <div className="space-y-4">
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Notification */}
      {notification.show && (
        <div className={`fixed top-4 right-4 z-50 flex items-center space-x-2 px-4 py-3 rounded-lg shadow-lg ${
          notification.type === 'success' 
            ? 'bg-green-100 border border-green-200 text-green-800'
            : 'bg-red-100 border border-red-200 text-red-800'
        }`}>
          {notification.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span className="text-sm font-medium">{notification.message}</span>
          <button
            onClick={() => setNotification({ show: false, type: '', message: '' })}
            className="text-current opacity-70 hover:opacity-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="space-y-6">
        {/* Add Category Button */}
        <div className="flex justify-end">
          <button
            onClick={handleAddCategory}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Add Category</span>
          </button>
        </div>

        {/* Categories List */}
        {categories.length > 0 ? (
          <div className="space-y-4">
            {categories.map((category) => (
              <div
                key={category.id}
                className="bg-gray-50 border border-gray-200 rounded-lg p-4"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    <div
                      className="w-12 h-12 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: category.color }}
                    >
                      <span className="text-white font-bold">
                        {category.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    
                    <div className="flex-1">
                      <h4 className="font-black text-black">{category.name}</h4>
                      <p className="text-sm text-gray-800 mb-3">{category.description}</p>

                      {category.assignment_rules?.auto_assign_enabled && (
                        <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded">
                          <span className="text-sm text-blue-800 font-medium">Auto-assignment enabled</span>
                          {category.assignment_rules.preferred_domains?.length > 0 && (
                            <p className="text-xs text-blue-600 mt-1">
                              Preferred domains: {category.assignment_rules.preferred_domains.join(', ')}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEditCategory(category)}
                      className="p-2 text-gray-400 hover:text-purple-600 transition-colors"
                      title="Edit Category"
                    >
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteCategory(category.id)}
                      className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                      title="Delete Category"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <BookOpen className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Task Categories</h3>
            <p className="text-gray-600 mb-4">
              Create your first task category to enable AI-powered task assignment and workflow optimization.
            </p>
            <button
              onClick={handleAddCategory}
              className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Create First Category
            </button>
          </div>
        )}
      </div>

      {/* Add/Edit Category Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingCategory ? 'Edit Category' : 'Create Task Category'}
              </h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="e.g., Web Design, Content Creation"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category Color
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                      className="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer"
                    />
                    <span className="text-sm text-gray-600">{formData.color}</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  rows={3}
                  placeholder="Describe what this category encompasses..."
                />
              </div>

              {/* Auto-Assignment Settings (Phase 2) */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-4">Auto-Assignment</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Configure how tasks in this category are automatically assigned
                </p>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Assignment Strategy
                    </label>
                    <select
                      value={formData.assignment_strategy || 'round_robin'}
                      onChange={(e) => setFormData(prev => ({ ...prev, assignment_strategy: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="none">No auto-assignment</option>
                      <option value="user">Specific User</option>
                      <option value="domain">Domain Expertise</option>
                      <option value="round_robin">Round Robin (Rotate)</option>
                      <option value="least_busy">Least Busy</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.assignment_strategy === 'user' && 'Always assign to a specific user'}
                      {formData.assignment_strategy === 'domain' && 'Assign based on domain expertise'}
                      {formData.assignment_strategy === 'round_robin' && 'Rotate assignments through team members'}
                      {formData.assignment_strategy === 'least_busy' && 'Assign to user with lowest workload'}
                      {(!formData.assignment_strategy || formData.assignment_strategy === 'none') && 'Let rules or client defaults handle assignment'}
                    </p>
                  </div>

                  {formData.assignment_strategy === 'user' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Default Assignee
                      </label>
                      <select
                        value={formData.default_assignee_id || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, default_assignee_id: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="">Select user...</option>
                        {availableUsers.map(member => (
                          <option key={member.user.id} value={member.user.id}>
                            {member.user.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {formData.assignment_strategy === 'domain' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Required Domain
                      </label>
                      <select
                        value={formData.domain_id || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, domain_id: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="">Select domain...</option>
                        {availableDomains.map(domain => (
                          <option key={domain.id} value={domain.id}>
                            {domain.name}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Tasks will be assigned to users with expertise in this domain
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex justify-end space-x-3 mt-8 pt-6 border-t">
              <button
                onClick={() => setShowAddModal(false)}
                disabled={saving}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCategory}
                disabled={saving || !formData.name.trim()}
                className="flex items-center space-x-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
              >
                {saving && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                <Save className="w-4 h-4" />
                <span>{saving ? 'Saving...' : (editingCategory ? 'Update Category' : 'Create Category')}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TaskCategoriesSettings;